import { useState, useEffect, useCallback } from 'react'
import { Search, FileText, Edit, Trash2, RefreshCw, Eye, Star, Upload, BookOpen } from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import libraryService, { LibraryDocument, LibraryTaxonomies } from '@/services/libraryService'
import { LibraryDocFormModal } from '@/components/library/LibraryDocFormModal'

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  artigo: 'Artigo Científico',
  manual: 'Manual Técnico',
  boletim: 'Boletim',
  relatorio: 'Relatório',
  tese: 'Tese/Dissertação',
  guia: 'Guia Prático',
}

export default function LibraryPage() {
  const { isAdmin, isGod } = usePermissions()
  const [documents, setDocuments] = useState<LibraryDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [activeTab, setActiveTab] = useState<'published' | 'draft'>('published')
  const [taxonomies, setTaxonomies] = useState<LibraryTaxonomies | null>(null)

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<LibraryDocument | null>(null)

  if (!isAdmin() && !isGod()) {
    return <Navigate to="/" replace />
  }

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true)
      const response = await libraryService.listDocuments({
        page,
        limit: 20,
        search: search || undefined,
        status: activeTab,
      })
      setDocuments(response.data)
      setTotalPages(response.pagination.total_pages)
    } catch (error) {
      console.error('Erro ao carregar documentos:', error)
      toast.error('Erro ao carregar documentos da biblioteca')
    } finally {
      setLoading(false)
    }
  }, [page, search, activeTab])

  const loadTaxonomies = useCallback(async () => {
    try {
      const data = await libraryService.getTaxonomies()
      setTaxonomies(data)
    } catch (error) {
      console.error('Erro ao carregar taxonomias:', error)
    }
  }, [])

  useEffect(() => {
    loadTaxonomies()
  }, [loadTaxonomies])

  useEffect(() => {
    setPage(1)
  }, [activeTab, search])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  const handleEditClick = async (docId: string) => {
    try {
      const doc = await libraryService.getDocumentById(docId)
      setSelectedDoc(doc)
      setShowEditModal(true)
    } catch (error) {
      console.error('Erro ao carregar documento:', error)
      toast.error('Erro ao carregar documento')
    }
  }

  const handleDelete = async (doc: LibraryDocument) => {
    if (!confirm(`Deseja realmente excluir "${doc.title}"?`)) return

    try {
      await libraryService.deleteDocument(doc.id)
      toast.success('Documento excluído')
      loadDocuments()
    } catch (error) {
      console.error('Erro ao excluir:', error)
      toast.error('Erro ao excluir documento')
    }
  }

  const handleCreateSubmit = async (formData: FormData) => {
    await libraryService.createDocument(formData)
    setShowCreateModal(false)
    toast.success('Documento criado com sucesso!')
    loadDocuments()
  }

  const handleEditSubmit = async (data: any) => {
    if (!selectedDoc) return
    await libraryService.updateDocument(selectedDoc.id, data)
    setShowEditModal(false)
    setSelectedDoc(null)
    toast.success('Documento atualizado!')
    loadDocuments()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Biblioteca Técnica
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Gerencie documentos científicos e técnicos da biblioteca
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadDocuments}
            className="p-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            title="Atualizar"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-500 font-medium text-sm"
          >
            <Upload className="h-4 w-4" />
            Upload PDF
          </button>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {(['published', 'draft'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab === 'published' ? 'Publicados' : 'Rascunhos'}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar documentos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-600 border-t-transparent" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Nenhum documento encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Fonte</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Páginas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Views</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{doc.title}</p>
                            {doc.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />}
                          </div>
                          {doc.author_name && (
                            <p className="text-xs text-gray-500 mt-0.5">{doc.author_name}</p>
                          )}
                          {doc.original_filename && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{doc.original_filename}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="text-sm text-gray-600">{doc.source || '—'}</span>
                    </td>
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <span className="text-sm text-gray-600">{doc.page_count}</span>
                    </td>
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <span className="text-sm text-gray-600 flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5" />
                        {doc.view_count}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEditClick(doc.id)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-600">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && taxonomies && (
        <LibraryDocFormModal
          title="Upload de Documento"
          taxonomies={taxonomies}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateSubmit}
          mode="create"
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedDoc && taxonomies && (
        <LibraryDocFormModal
          title="Editar Documento"
          document={selectedDoc}
          taxonomies={taxonomies}
          onClose={() => { setShowEditModal(false); setSelectedDoc(null) }}
          onSubmit={handleEditSubmit}
          mode="edit"
        />
      )}
    </div>
  )
}
